/** Quote dates/HH:mm values are event wall time; startsAt/endsAt, when present,
 * are instants and must be projected through the event's zone for display. */
export function quoteShiftInEventZone<T extends Record<string, any>>(shift: T, eventZone?: string): T {
  if (!eventZone) return shift;
  const projected = { ...shift };
  for (const [instantField, dateField, timeField] of [['startsAt', 'startDate', 'startTime'], ['endsAt', 'endDate', 'endTime']]) {
    const instant = shift[instantField];
    if (!instant) continue;
    const date = new Date(instant);
    if (!Number.isFinite(date.getTime())) continue;
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: eventZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(date);
      const part = (type: string) => parts.find(item => item.type === type)?.value;
      (projected as Record<string, any>)[dateField] = `${part('year')}-${part('month')}-${part('day')}`;
      (projected as Record<string, any>)[timeField] = `${part('hour')}:${part('minute')}`;
    } catch { /* Retain saved wall values if the event has an invalid zone. */ }
  }
  return projected;
}

/** Client proposal end dates follow its start date and clocks, including copied rows. */
export function withClientQuoteEndDate<T extends Record<string, any>>(shift: T): T {
  const day = String(shift.startDate ?? '').split('T')[0];
  const date = new Date(`${day}T00:00:00.000Z`);
  const clock = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !Number.isFinite(date.getTime()) ||
      date.toISOString().slice(0, 10) !== day || !clock.test(String(shift.startTime)) || !clock.test(String(shift.endTime))) return shift;
  const startClock = String(shift.startTime).length === 5 ? `${shift.startTime}:00` : String(shift.startTime);
  const endClock = String(shift.endTime).length === 5 ? `${shift.endTime}:00` : String(shift.endTime);
  if (endClock < startClock) date.setUTCDate(date.getUTCDate() + 1);
  return { ...shift, endDate: date.toISOString().slice(0, 10) };
}
